import { WatchQueryFetchPolicy } from '@apollo/client';
import { useEffect, useState } from 'react';

import { useListProjectsTasksQuery, ProjectTask } from '@graphql/generated';
/**
 * Retrieves a list of project tags
 */

interface ListProjectsTasksQueryProps {
  archivesOnly?: boolean;
  onCompleted?: () => void;
  onError?: () => void;
  first?: number;
  last?: number;
  after?: string;
  before?: string;
  filterTeamsProjects?: boolean;
  chatId?: string;
  fetchPolicy?: WatchQueryFetchPolicy;
  excludeCompleted?: boolean;
  collaboratorsOnly?: boolean;
  skip?: boolean;
}

export const useListProjectsTasksFromQuery = (
  props?: ListProjectsTasksQueryProps
) => {
  const {
    onCompleted = undefined,
    onError = undefined,
    first = undefined,
    last = undefined,
    after = undefined,
    before = undefined,
    excludeCompleted = false,
    collaboratorsOnly = false,
    chatId = undefined,
    fetchPolicy = 'cache-and-network',
    skip = false,
  } = props || {};

  const [refreshing, setRefreshing] = useState(false);

  const { data, loading, error, refetch, fetchMore } =
    useListProjectsTasksQuery({
      variables: {
        first,
        last,
        before,
        after,
        excludeCompleted,
        collaboratorsOnly,
        chatId,
      },
      fetchPolicy: fetchPolicy,
      skip,
    });

  useEffect(() => {
    if (!loading) setRefreshing(false);
  }, [loading]);
  useEffect(() => {
    if (data) onCompleted?.();
  }, [data]);
  useEffect(() => {
    if (error) onError?.();
  }, [error]);

  const { listProjectsTasks } = data || { listProjectsTasks: undefined };

  const fetchMoreFromCursor = () => {
    if (!projects || !pageInfo?.hasNextPage || loading) return;
    const cursor = pageInfo?.endCursor;
    fetchMore({
      variables: {
        after: cursor,
        first,
        chatId,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const newEdges = fetchMoreResult.listProjectsTasks.edges;
        const pageInfo = fetchMoreResult.listProjectsTasks.pageInfo;
        if (!newEdges?.length) return previousResult;

        // TA-10502 follow-up: onEndReached can fire more than once for the
        // same page before `loading` flips true (React Native FlatList/
        // SectionList quirk, worse once more re-renders are in flight), so
        // guard the merge itself rather than relying on the caller only
        // calling fetchMore once per page — dedupe by id so a repeat page
        // doesn't render the same project twice.
        const previousEdges = previousResult.listProjectsTasks.edges;
        const seenIds = new Set(previousEdges.map((edge) => edge?.node?.id));
        const dedupedNewEdges = newEdges.filter(
          (edge) => edge?.node?.id && !seenIds.has(edge.node.id)
        );

        return {
          listProjectsTasks: {
            __typename: previousResult.listProjectsTasks.__typename,
            edges: [...previousEdges, ...dedupedNewEdges],
            pageInfo,
          },
        };
      },
    });
  };

  const { edges = [], pageInfo } = listProjectsTasks || { edges: [] };
  // TODO: This needs to be more properly typed to the returned graphql value.
  const projects =
    (edges || []).reduce((acc, curr) => {
      if (curr?.node !== undefined) {
        return [...acc, curr.node as ProjectTask];
      } else {
        return acc;
      }
    }, [] as ProjectTask[]) || [];

  return {
    projectsTasks: projects,
    loading,
    error,
    pageInfo,
    refetch,
    fetchMore,
    fetchMoreFromCursor,
    refreshing,
    setRefreshing,
  };
};
